import axios from 'axios';
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { FileSignature, CheckCircle, AlertTriangle, History, Loader2 } from "lucide-react";

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

export const EmployeeContrats = ({ employeeId, employeeNom }) => {
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (employeeId) loadData();
  }, [employeeId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statusRes, histRes] = await Promise.all([
        axios.get(`${API_URL}/api/contracts/status/${employeeId}`),
        axios.get(`${API_URL}/api/contracts/history/${employeeId}`)
      ]);
      const statusData = statusRes.data;
      const histData = histRes.data;
      setStatus(statusData);
      setHistory(histData.signatures || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-12">
      <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Statut actuel */}
      <Card className={status?.contrat_valide ? "" : "border-orange-300 bg-orange-50"}>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <FileSignature className="w-5 h-5 text-purple-600" />
            Statut du contrat — {employeeNom}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2">
            {status?.contrat_valide
              ? <><CheckCircle className="w-5 h-5 text-green-500" /><span className="font-medium text-green-700">Contrat valide</span></>
              : <><AlertTriangle className="w-5 h-5 text-orange-500" /><span className="font-medium text-orange-700">Signature requise</span></>}
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-slate-500">Statut employé</p>
              <Badge variant={status?.permanence === 'saisonnier' ? 'outline' : 'default'} className="mt-0.5">
                {status?.permanence === 'saisonnier' ? 'Saisonnier' : 'Permanent'}
              </Badge>
            </div>
            <div>
              <p className="text-slate-500">Prochaine expiration</p>
              <p className="font-medium">
                {status?.date_expiration ? new Date(status.date_expiration).toLocaleDateString('fr-CA', { year: 'numeric', month: 'long', day: 'numeric' }) : '—'}
              </p>
            </div>
            <div>
              <p className="text-slate-500">Règle de re-signature</p>
              <p className="font-medium text-xs text-slate-600">
                {status?.permanence === 'saisonnier' ? 'Chaque 15 avril' : 'Chaque 1er janvier'}
              </p>
            </div>
            <div>
              <p className="text-slate-500">Signatures totales</p>
              <p className="font-medium">{status?.nombre_signatures}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Historique */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <History className="w-5 h-5 text-slate-600" />
            Historique des signatures
          </CardTitle>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <FileSignature className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p>Aucune signature enregistrée</p>
              <p className="text-xs text-slate-400 mt-1">L'employé pourra signer depuis son Mon Espace</p>
            </div>
          ) : (
            <div className="space-y-3">
              {history.map((sig, idx) => (
                <div key={sig.id || idx} className="p-4 border rounded-lg bg-slate-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">{sig.template_titre || 'Contrat de travail'}</p>
                      <p className="text-xs text-slate-500">
                        Version {sig.template_version} — Signé par: <strong>{sig.signature_nom}</strong>
                      </p>
                      <p className="text-xs text-slate-400">
                        {new Date(sig.date_signature).toLocaleDateString('fr-CA', {
                          year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
                        })}
                      </p>
                    </div>
                    <div className="text-right space-y-1">
                      <Badge className="bg-green-100 text-green-700 text-xs">Signé</Badge>
                      {sig.signature_url && (
                        <div>
                          <img src={sig.signature_url} alt="Signature" className="h-10 border rounded bg-white mt-1" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
